import { Switch } from "react-animated-switch";
import { PiCarDuotone } from "react-icons/pi";
import styles from "./Parking.module.scss";
import { TiMinus } from "react-icons/ti";
import { TiPlus } from "react-icons/ti";
import React from "react";

interface ParkingItem {
  parkingId: number;
  amenityName: string;
  noofvehicle: number;
  availableNoofvehicle: number;
  cost: number;
  terms: string;
  image: string;
  amenityId: number;
}

interface ParkingSelection {
  parkingId: string;
  noOfVehicle: number;
  notifyParking: boolean;
}

interface Props {
  parkings: ParkingItem[];
  parkingSelections: ParkingSelection[];
  setParkingSelections: React.Dispatch<
    React.SetStateAction<ParkingSelection[]>
  >;
}

function Parking(props: Props) {
  const { parkings, parkingSelections, setParkingSelections } = props;

  const getSel = (parkingId: number) =>
    parkingSelections.find((s) => s.parkingId === String(parkingId)) ?? {
      parkingId: String(parkingId),
      noOfVehicle: 0,
      notifyParking: false,
    };

  const updateSel = (parkingId: number, patch: Partial<ParkingSelection>) => {
    setParkingSelections((prev) => {
      const id = String(parkingId);

      const i = prev.findIndex((s) => s.parkingId === id);

      const cur =
        i >= 0
          ? prev[i]
          : {
            parkingId: id,
            noOfVehicle: 0,
            notifyParking: false,
          };

      const next = { ...cur, ...patch };

      return i >= 0
        ? prev.map((s, j) => (j === i ? next : s))
        : [...prev, next];
    });
  };

  const changeCount = (opt: ParkingItem, delta: number) => {
    const s = getSel(opt.parkingId);
    const max = opt.noofvehicle ?? 0;
    const next = s.noOfVehicle + delta;

    if (next >= 0 && next <= max) {
      updateSel(opt.parkingId, { noOfVehicle: next });
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <p className={styles.heading}>Parking</p>
        <p className={styles.subHeading}>
          Select parking spaces for your booking
        </p>
      </div>

      {parkings?.map((opt) => {
        const s = getSel(opt.parkingId);

        const overLimit = s.noOfVehicle > (opt.availableNoofvehicle ?? 0);

        const noneAvailable = (opt.availableNoofvehicle ?? 0) === 0;
        const parkingAvailable: boolean = (opt.availableNoofvehicle ?? 0) > 0;

        const showNotifySwitch = overLimit;

        const hasUserAddedParking = parkingSelections.some(
          (p) => p.parkingId === String(opt.parkingId),
        );

        return (
          <div
            key={opt.parkingId}
            className={`${styles.parkingGroupContainer} ${hasUserAddedParking ? styles.userAddedParking : ""}`}
          >
            <div className={styles.parkingGroup}>
              <div className={styles.iconContainer}>
                <PiCarDuotone
                  className={`${styles.icon} ${parkingAvailable ? styles.parkingAvailableIcon : ""}`}
                />
              </div>
              <div className={styles.parkingContentMainContainer}>
                <div className={styles.parkingContentContainer}>
                  <div className={styles.labelContainer}>
                    <p className={styles.label}>{opt.amenityName}</p>
                    {(overLimit || noneAvailable) && (
                      <div className={styles.notAvailableContainer}>
                        <p className={styles.label}>Unavailable</p>
                      </div>
                    )}
                  </div>
                  <div className={styles.availableSpacesContainer}>
                    <p className={styles.spaceText}>{opt.availableNoofvehicle ?? 0} Spaces Available</p>
                  </div>

                  <div className={styles.barContainer}>
                    <div className={styles.bar}>
                      <div
                        className={styles.barFill}
                        style={{
                          width: `${((opt.availableNoofvehicle ?? 0) / (opt.noofvehicle ?? 0)) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <p className={styles.barText}>
                      {opt.availableNoofvehicle ?? 0} / {opt.noofvehicle ?? 0}{" "}
                      Spaces
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.counterContainer}>
                <button
                  className={styles.counterButton}
                  disabled={noneAvailable}
                  onClick={() => changeCount(opt, -1)}
                >
                  <TiMinus />
                </button>
                <p className={styles.counterValue}>{s.noOfVehicle}</p>
                <button
                  className={styles.counterButton}
                  disabled={noneAvailable}
                  onClick={() => changeCount(opt, 1)}
                >
                  <TiPlus />
                </button>
              </div>
              <div className={styles.priceContainer}>
                <p>${(opt.cost ?? 0) * s.noOfVehicle}</p>
              </div>
            </div>
            {showNotifySwitch && (
              <div className={styles.noticeContainer}>
                <div className={styles.switchContainer}>
                  <Switch
                    inActiveTrackColor="#DFDFDF"
                    activeTrackColor="#C0F496"
                    inActiveThumbColor="#000000"
                    activeThumbColor="#000000"
                    checked={s.notifyParking}
                    onChange={(val) =>
                      updateSel(opt.parkingId, {
                        notifyParking: val,
                      })
                    }
                  />
                </div>

                <p className={styles.label}>
                  Notify me when parking is available
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Parking;
